import { useState, useEffect } from 'react';
import { Upload, CheckCircle2, Download, ExternalLink, FileArchive, Loader2, AlertCircle, Palette } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/ui/use-toast';
import { extractCanvaZip } from '@/lib/email/canva-zip-handler';
import { parseCanvaHtml, type TextBlock, type Button as ParsedButton } from '@/lib/email/canva-html-parser';
import { CanvaTemplateMappingDialog } from './CanvaTemplateMappingDialog';
import { cn } from '@/lib/core/utils';

interface EditingTemplate {
  id: string;
  name: string;
  configuration: any;
}

interface CanvaImportDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  onZipExtracted?: (html: string, textBlocks: TextBlock[], buttons: ParsedButton[]) => void;
  businessId?: string;
  editingTemplate?: EditingTemplate | null;
}

export function CanvaImportDialog({ open, onClose, onSaved, onZipExtracted, businessId, editingTemplate }: CanvaImportDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('upload');

  // ZIP file handling state
  const [canvaMappingDialogOpen, setCanvaMappingDialogOpen] = useState(false);
  const [canvaTextBlocks, setCanvaTextBlocks] = useState<TextBlock[]>([]);
  const [canvaButtons, setCanvaButtons] = useState<ParsedButton[]>([]);
  const [canvaHtmlPreview, setCanvaHtmlPreview] = useState('');

  // Handle editing an existing template
  useEffect(() => {
    if (open && editingTemplate) {
      const config = editingTemplate.configuration;
      if (config?.html) {
        const { textBlocks, buttons } = parseCanvaHtml(config.html);

        if (config.canvaBlocks) {
          textBlocks.forEach((block, index) => {
            const savedBlock = config.canvaBlocks.find((b: any) => b.index === index || b.type === block.type);
            if (savedBlock) {
              block.label = savedBlock.label;
              block.originalContent = savedBlock.originalContent || block.content;
            }
          });
        }

        setCanvaTextBlocks(textBlocks);
        setCanvaButtons(buttons);
        setCanvaHtmlPreview(config.html);
        setCanvaMappingDialogOpen(true);
      }
    }
  }, [open, editingTemplate]);

  const handleFileSelect = async (file: File) => {
    if (!file.name.endsWith('.zip')) {
      toast.error('Invalid file type', {
        description: 'Please upload a ZIP file exported from Canva',
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large', {
        description: 'File must be smaller than 10MB',
      });
      return;
    }

    setSelectedFile(file);
    setIsProcessing(true);

    try {
      if (!businessId) {
        toast.error('Business context required', {
          description: 'Unable to import ZIP without business context',
        });
        setIsProcessing(false);
        return;
      }

      const result = await extractCanvaZip(file, businessId);

      if (!result.success) {
        toast.error('ZIP extraction failed', {
          description: result.error.message,
        });
        setIsProcessing(false);
        return;
      }

      const parsed = parseCanvaHtml(result.data.html);

      toast.success('Design imported!', {
        description: `Found ${parsed.textBlocks.length} text blocks and ${parsed.buttons.length} buttons`,
      });

      if (onZipExtracted) {
        onZipExtracted(result.data.html, parsed.textBlocks, parsed.buttons);
        handleClose();
        return;
      }

      setCanvaTextBlocks(parsed.textBlocks);
      setCanvaButtons(parsed.buttons);
      setCanvaHtmlPreview(result.data.html);
      setCanvaMappingDialogOpen(true);
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error('Processing failed', {
        description: 'Failed to process file',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCanvaSaved = () => {
    setCanvaMappingDialogOpen(false);
    handleClose();
    onSaved?.();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setCanvaMappingDialogOpen(false);
    setCanvaTextBlocks([]);
    setCanvaButtons([]);
    setCanvaHtmlPreview('');
    onClose();
  };

  const showImportDialog = open && !editingTemplate;

  const steps = [
    {
      number: 1,
      title: "Create in Canva",
      description: "Design your email using Canva's templates or start from scratch. Use the 'Email' preset for best results.",
    },
    {
      number: 2,
      title: "Download HTML and Images",
      description: "Click Share → Download → Select 'HTML and images' → Download. This will give you a ZIP file.",
    },
    {
      number: 3,
      title: "Upload ZIP here",
      description: "Drag and drop the ZIP file into the upload area. We'll extract and process everything automatically.",
    },
  ];

  return (
    <>
      <Dialog open={showImportDialog} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-primary" />
              Import from Canva
            </DialogTitle>
            <DialogDescription>
              Import your Canva designs as email templates
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload File
              </TabsTrigger>
              <TabsTrigger value="instructions" className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                How to Export
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="space-y-4 mt-4">
              {/* Drop Zone */}
              <div
                className={cn(
                  "relative cursor-pointer rounded-lg border-2 border-dashed transition-all duration-200",
                  isDragging
                    ? "border-primary bg-primary/5 scale-[1.01]"
                    : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('html-file-input')?.click()}
              >
                <div className="p-8 text-center">
                  <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-muted flex items-center justify-center">
                    {isProcessing ? (
                      <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    ) : (
                      <FileArchive className={cn(
                        "w-8 h-8 transition-colors",
                        isDragging ? "text-primary" : "text-muted-foreground"
                      )} />
                    )}
                  </div>

                  <h3 className="text-lg font-semibold mb-1">
                    {isProcessing ? 'Processing your design...' : isDragging ? 'Drop it here!' : 'Drop your Canva export'}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {isProcessing
                      ? 'Extracting images and optimizing HTML'
                      : 'Upload the ZIP file from Canva'
                    }
                  </p>

                  {!isProcessing && (
                    <Button variant="outline" size="sm" className="pointer-events-none">
                      <Upload className="w-4 h-4 mr-2" />
                      or click to browse
                    </Button>
                  )}

                  {selectedFile && !isProcessing && (
                    <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/30 text-sm text-green-700 dark:text-green-400">
                      <CheckCircle2 className="w-4 h-4" />
                      {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </div>
                  )}
                </div>

                <input
                  id="html-file-input"
                  type="file"
                  accept=".zip"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </div>

            </TabsContent>

            <TabsContent value="instructions" className="space-y-4 mt-4">
              {/* Steps */}
              <div className="space-y-3">
                {steps.map((step, index) => (
                  <div
                    key={step.number}
                    className="relative p-4 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                        {step.number}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold mb-1">{step.title}</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tips */}
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Tips:</strong>
                  <ul className="mt-2 space-y-1 text-sm">
                    <li>• Use Canva's email templates for better compatibility</li>
                    <li>• Keep designs under 600px wide for mobile</li>
                    <li>• The ZIP export includes all images automatically</li>
                  </ul>
                </AlertDescription>
              </Alert>

              {/* Browse Templates Link */}
              <a
                href="https://www.canva.com/templates/?query=email"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <ExternalLink className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Browse Canva Templates</p>
                    <p className="text-sm text-muted-foreground">Find beautiful email designs</p>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </a>
            </TabsContent>
          </Tabs>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            {activeTab === 'instructions' && (
              <Button onClick={() => setActiveTab('upload')}>
                <Upload className="w-4 h-4 mr-2" />
                Go to Upload
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Canva Template Mapping Dialog */}
      {businessId && (
        <CanvaTemplateMappingDialog
          open={canvaMappingDialogOpen}
          onOpenChange={(isOpen) => {
            setCanvaMappingDialogOpen(isOpen);
            if (!isOpen) {
              setCanvaTextBlocks([]);
              setCanvaButtons([]);
              setCanvaHtmlPreview('');
              setSelectedFile(null);
              if (editingTemplate) {
                onClose();
              }
            }
          }}
          textBlocks={canvaTextBlocks}
          buttons={canvaButtons}
          htmlPreview={canvaHtmlPreview}
          businessId={businessId}
          onSaved={handleCanvaSaved}
          onCancel={() => {
            setCanvaMappingDialogOpen(false);
            setCanvaTextBlocks([]);
            setCanvaButtons([]);
            setCanvaHtmlPreview('');
            setSelectedFile(null);
            if (editingTemplate) {
              onClose();
            }
          }}
          editingTemplate={editingTemplate}
        />
      )}
    </>
  );
}
